// Modules
import { useBasicModal } from 'hooks/useBasicModal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// UI
import { Button } from 'components/catalyst/button';
import { SidebarItem } from 'components/catalyst/sidebar';
import LogoutModal from 'components/LogoutModal';
import { Avatar } from 'components/catalyst/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from 'components/catalyst/dropdown';
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  UserIcon,
  KeyIcon,
} from '@heroicons/react/16/solid';
import ChangePasswordModal from 'components/auth/ChangePasswordModal';

// Utils
import { useAppSelector } from 'app/store';
import { selectAdminUser } from 'features/auth/authSlice';

interface ExtraMenuItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  onClick?: () => void;
}

interface Props {
  type?: 'sidebar';
  navigateOnLogout?: string;
  noButtons?: boolean;
  isMinimized?: boolean;
  variant?: 'default' | 'light';
  extraItems?: ExtraMenuItem[];
}

const UserMenu = ({type, navigateOnLogout, noButtons, isMinimized = false, variant = 'default', extraItems}: Props) => {
  const isLight = variant === 'light';
  const { t } = useTranslation('common');
  const user = useAppSelector(selectAdminUser);

  const navigate = useNavigate();

  const {showModal, handleCloseModal, handleOpenModal} = useBasicModal();

  const {
    showModal: showChangePassModal,
    handleCloseModal: handleCloseChangePassModal,
    handleOpenModal: handleOpenChangePassModal,
  } = useBasicModal();

  const handleViewProfile = () => navigate(`/profile/${user?.company}`);

  return (
    <>
      <Dropdown>
        <DropdownButton
          as={(type === 'sidebar' ? SidebarItem : Button) as any}
          plain={type === 'sidebar' ? undefined : true}
          className={isMinimized && type === 'sidebar' ? 'justify-center' : ''}>
          <span className={`flex min-w-0 items-center ${isMinimized && type === 'sidebar' ? 'justify-center' : 'gap-3'}`}>
            <Avatar className="size-10 p-2" square alt="">
              <UserIcon className={isLight ? "text-white/80 group-hover:text-white" : "text-zinc-500 dark:text-zinc-400 group-hover:text-white"} />
            </Avatar>

            {type === 'sidebar' && !isMinimized && (
              <span className="min-w-0">
                <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white group-hover:text-white">
                  {`${user?.firstName} ${user?.lastName}`}
                </span>

                <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400 group-hover:text-white/80">
                  {user?.email}
                </span>
              </span>
            )}
          </span>

          {type === 'sidebar' && !isMinimized && <ChevronUpIcon />}
        </DropdownButton>

        <DropdownMenu className="min-w-64" anchor="top start">
          {!noButtons && (
            <>
              <DropdownItem onClick={handleViewProfile} className='gap-2 cursor-pointer'>
                <UserIcon className='h-5 w-5' />
                <DropdownLabel>{t('SIDEBAR_MY_PROFILE')}</DropdownLabel>
              </DropdownItem>

              <DropdownItem onClick={handleOpenChangePassModal} className='gap-2 cursor-pointer'>
                <KeyIcon className='h-5 w-5' />
                <DropdownLabel>{t('PASSWORD_CHANGE_BUTTON')}</DropdownLabel>
              </DropdownItem>

              <DropdownDivider />
            </>
          )}

          {extraItems?.map(({ label, icon: Icon, path, onClick }) => (
            <DropdownItem
              key={label}
              onClick={() => {
                if (onClick) {
                  onClick();
                } else if (path) {
                  navigate(path);
                }
              }}
              className='gap-2 cursor-pointer'
            >
              <Icon className='h-5 w-5' />
              <DropdownLabel>{label}</DropdownLabel>
            </DropdownItem>
          ))}

          {extraItems && extraItems.length > 0 && <DropdownDivider />}

          <DropdownItem onClick={handleOpenModal} className='gap-2 cursor-pointer'>
            <ArrowRightStartOnRectangleIcon className='h-5 w-5' />
            <DropdownLabel>{t('SIDEBAR_SIGN_OUT')}</DropdownLabel>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <LogoutModal
        isOpen={showModal}
        handleCloseModal={handleCloseModal}
        navigateTo={navigateOnLogout}
      />

      <ChangePasswordModal
        isOpen={showChangePassModal}
        onClose={handleCloseChangePassModal}
      />
    </>
  );
};

export default UserMenu;
